// Shared "Load older" paging state. See lib/email/loadOlder.ts for the rules and for why
// the three pre-v1.5.66 copies of this logic disagreed with each other.
//
// Pages are held in COMPONENT STATE, deliberately not in React Query. The account page's
// ["account-emails", id] key is invalidated by invalidateEmailSurfaces on every claim, pin,
// flag and read, and ["inbox"] is refetched on a 60s poll — anything written into either
// cache is clobbered within one round trip. Local state is what makes a loaded page survive
// the next action the user takes.
//
// Fetching is injectable because the three surfaces do not share an endpoint: /inbox and the
// pop-out's shared pane page /api/inbox/older, while the pop-out's My Mail pane pages
// /api/inbox/mine (which alone returns a populated `filtered` bucket).

import { useCallback, useMemo, useReducer, useRef } from "react";
import type { InboxMessage } from "@/lib/api";
import { emailApi } from "@/lib/api";
import { planOlderFetch, applyOlderPages, EMPTY_OLDER_BAG, type OlderBag } from "@/lib/email/loadOlder";

export interface OlderPage {
  messages: InboxMessage[];
  filtered?: InboxMessage[];
}

export interface LoadOlderTarget {
  mailbox: string;
  /** Server-owned rows for this mailbox — the cursor floor before any page is loaded. */
  baseRows: readonly { id: string; receivedDateTime?: string | null }[];
}

export interface LoadOlderState {
  rowsByMailbox: Readonly<Record<string, InboxMessage[]>>;
  filteredByMailbox: Readonly<Record<string, InboxMessage[]>>;
  /** Every loaded older row across mailboxes — the convenience view for flat-list surfaces. */
  rows: InboxMessage[];
  /** `${epochKey}#${version}` — the sound useMemo dependency (mirrors StickyPill.token). */
  token: string;
  isLoading: (mailbox: string) => boolean;
  isExhausted: (mailbox: string) => boolean;
  loadingAny: boolean;
  /** True only once every mailbox that has been paged reports exhausted. */
  exhaustedAll: (mailboxes: readonly string[]) => boolean;
  /**
   * Page one step older for each target, in parallel, each with its own cursor.
   * Resolves with how many rows actually landed, so a caller holding a render cap can widen
   * it — without that, a fetched page sorts below the cap and the click looks like a no-op.
   */
  loadOlder: (targets: readonly LoadOlderTarget[]) => Promise<{ added: number }>;
  /** Discard every loaded page. Wire to the manual Refresh ONLY — never to a poll. */
  reset: () => void;
  /**
   * Drop one row from every locally-held page. Used for optimistic removal (My Mail's
   * "Move to..." takes a message out of the list ahead of the server confirming the move);
   * without it, a moved message keeps rendering in a loaded older page until a refetch.
   */
  removeRow: (id: string) => void;
}

type Bag = OlderBag<InboxMessage>;

const EMPTY = EMPTY_OLDER_BAG as Bag;

export function useLoadOlder(opts?: {
  /** Defaults to /api/inbox/older. My Mail injects a /api/inbox/mine adapter. */
  fetchPage?: (args: { mailbox: string; before: string }) => Promise<OlderPage>;
  /** Render-phase reset on change. "" (the default) never resets. */
  epochKey?: string;
  /** Console prefix, so "[inbox]" vs "[inbox/mine]" survives the extraction. */
  logTag?: string;
  /** Surfaced to the caller so each surface keeps its own toast. */
  onError?: (err: unknown) => void;
}): LoadOlderState {
  const epochKey = opts?.epochKey ?? "";
  const logTag = opts?.logTag ?? "[inbox]";
  const fetchPage = opts?.fetchPage;
  const onError = opts?.onError;

  // ENG-114: the bag lives in a REF that is written synchronously, and React state is used
  // only to schedule a re-render. Before this, the bag was useState and loadOlder computed
  // "what to fetch" inside a setBag updater. React 19 runs an updater synchronously only when
  // the component has no pending update; otherwise it defers it to the next render. On the
  // pop-out (which re-renders constantly) the fetch list was therefore still empty when read,
  // no request fired, and the deferred updater then set loading=true forever. Every decision
  // below reads bagRef.current and goes through the pure transitions in lib/email/loadOlder.
  const bagRef = useRef<Bag>(EMPTY);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const commit = useCallback((next: Bag) => {
    if (next === bagRef.current) return;
    bagRef.current = next;
    rerender();
  }, []);

  // Render-phase reset (the useStickyPill ref pattern): an effect-based reset would paint one
  // frame of the previous account's older rows. Writing a ref during render is safe here
  // because the reset is idempotent -- a discarded render that re-runs it lands the same EMPTY.
  const epochRef = useRef(epochKey);
  if (epochRef.current !== epochKey) {
    epochRef.current = epochKey;
    bagRef.current = EMPTY;
  }
  const bag = bagRef.current;

  const reset = useCallback(() => commit(EMPTY), [commit]);

  const removeRow = useCallback(
    (id: string) => {
      const prev = bagRef.current;
      let touched = false;
      const strip = (src: Record<string, InboxMessage[]>) => {
        const out: Record<string, InboxMessage[]> = {};
        for (const [mb, list] of Object.entries(src)) {
          const next = list.filter((m) => m.id !== id);
          if (next.length !== list.length) touched = true;
          out[mb] = next;
        }
        return out;
      };
      const rows = strip(prev.rows);
      const filtered = strip(prev.filtered);
      if (touched) commit({ ...prev, rows, filtered, version: prev.version + 1 });
    },
    [commit],
  );

  const loadOlder = useCallback(
    async (targets: readonly LoadOlderTarget[]): Promise<{ added: number }> => {
      const fetcher =
        fetchPage ??
        (({ mailbox, before }: { mailbox: string; before: string }) =>
          emailApi.getOlderInboxMessages(mailbox, before));

      // Decided synchronously from the last committed bag; loading is set if and only if a
      // request is about to be made for that mailbox.
      const plan = planOlderFetch(bagRef.current, targets);
      if (plan.pending.length === 0) return { added: 0 };
      commit(plan.next);

      const results = await Promise.all(
        plan.pending.map(async (p) => {
          try {
            const page = await fetcher(p);
            return { mailbox: p.mailbox, page, err: null as unknown };
          } catch (err) {
            console.error(`${logTag} load older failed:`, err);
            return { mailbox: p.mailbox, page: null, err };
          }
        }),
      );

      const failed = results.find((r) => r.err !== null);
      if (failed) onError?.(failed.err);

      // Folded into whatever the bag is NOW (a reset or a removeRow may have landed while the
      // request was in flight), not the bag the plan was made from.
      const applied = applyOlderPages(bagRef.current, targets, results);
      commit(applied.next);
      return { added: applied.added };
    },
    [fetchPage, logTag, onError, commit],
  );

  const rows = useMemo(
    () => Object.values(bag.rows).flat(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bag.rows, bag.version],
  );

  const isLoading = useCallback((mailbox: string) => !!bag.loading[mailbox], [bag.loading]);
  const isExhausted = useCallback((mailbox: string) => !!bag.exhausted[mailbox], [bag.exhausted]);
  const exhaustedAll = useCallback(
    (mailboxes: readonly string[]) => mailboxes.length > 0 && mailboxes.every((mb) => !!bag.exhausted[mb]),
    [bag.exhausted],
  );

  return {
    rowsByMailbox: bag.rows,
    filteredByMailbox: bag.filtered,
    rows,
    token: `${epochKey}#${bag.version}`,
    isLoading,
    isExhausted,
    loadingAny: Object.values(bag.loading).some(Boolean),
    exhaustedAll,
    loadOlder,
    reset,
    removeRow,
  };
}
